import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { ArrowRight } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";
import { GradientSparkle } from "./gradient-sparkle";

/**
 * ── Known gaps vs the Figma sheet (#455) ────────────────────────────────────
 * Recorded rather than fixed: the six original variants are rendered by Venus
 * and Neptune today, so changing the base class or `outline` repaints both apps
 * and is its own decision.
 *
 *  1. `outline` border hue. Figma specifies a Deep Blue border; this uses
 *     `border-input` (`--input`, #CDD1DF, Dark Blue 1) — a neutral, not the
 *     brand blue.
 *  2. Font weight. The base is `font-medium` (500). DESIGN.md assigns Sora
 *     *Semibold* to buttons, and the UI type scale's `label` token is 600.
 *  3. Radius / padding / size are UNVERIFIED against Figma.
 *     docs/design-library/01-figma-components.md is an inventory with no
 *     measurements, so `rounded-full h-10 px-4 py-2 text-sm` has not been
 *     checked against the source file.
 *
 * The variants added for Figma (`cta`, `ai`) are new, so they are free to
 * differ from the base — `cta` is semibold and a rounded rectangle.
 */

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-muted hover:text-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "text-foreground hover:bg-muted",
        link: "text-primary underline-offset-4 hover:underline",

        // Figma "Order / CTA" — the #436 shiny CTA. Full width, a rounded
        // RECTANGLE (not the pill the base class sets), leading slot for a
        // SystemBadge, trailing round arrow disc. `justify-between` pushes the
        // slots to the edges with the label between them.
        cta: "w-full justify-between rounded-md bg-primary font-semibold text-primary-foreground hover:bg-primary/90",

        // Figma "AI-identified" — an outline button marked as AI-derived. The
        // purple lives in the sparkle (see GradientSparkle), never in the fill.
        ai: "border border-input bg-background text-foreground hover:bg-muted",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3",
        lg: "h-11 px-8",
        icon: "h-10 w-10",

        cta: "h-9 pl-2 pr-2",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

/**
 * The round arrow disc at the trailing edge of the Order/CTA button.
 *
 * White disc, Deep Blue glyph — `--primary-foreground` / `--primary` rather
 * than a literal white, which keeps it correct on the CTA's fill in both
 * themes (`--primary` is deliberately theme-stable, see theme.css).
 */
export function ButtonArrow({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex size-[22px] shrink-0 items-center justify-center rounded-full bg-primary-foreground text-primary",
        className,
      )}
      aria-hidden="true"
    >
      <ArrowRight className="size-2.5" />
    </span>
  );
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * Content before the label — e.g. `<SystemBadge>SAP</SystemBadge>` on the CTA,
   * or an icon. Ignored when `asChild` is set (see below).
   */
  leading?: React.ReactNode;
  /**
   * Content after the label — e.g. `<ConfidencePill value={94} />`. On
   * `variant="cta"` this defaults to `<ButtonArrow />`; pass `null` to drop it.
   * Ignored when `asChild` is set (see below).
   */
  trailing?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, leading, trailing, children, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    // `variant="ai"` carries its marker by default so the variant stands on its
    // own; an explicit `leading` (including `null`) still wins.
    const resolvedLeading =
      leading === undefined && variant === "ai" ? (
        <GradientSparkle className="h-4 w-4 shrink-0" />
      ) : (
        leading
      );
    const resolvedTrailing =
      trailing === undefined && variant === "cta" ? <ButtonArrow /> : trailing;

    // Radix `Slot` clones a SINGLE child, so wrapping `children` alongside slot
    // content would break `asChild`. Under `asChild` the children pass through
    // untouched and the slots are dropped — keeping the pre-#455 behaviour
    // byte-identical for every existing call site.
    const hasSlots =
      !asChild && (resolvedLeading != null || resolvedTrailing != null);

    return (
      <Comp
        // Default to type="button" so buttons inside forms don't submit them;
        // an explicit type in props still wins (spread comes after).
        type={asChild ? undefined : "button"}
        // `gap-2` only when slots actually render, so a plain button keeps
        // exactly the classes it had before #455. Passed separately from
        // `buttonVariants` so a caller-supplied `className` still wins.
        className={cn(
          buttonVariants({ variant, size }),
          hasSlots && "gap-2",
          className,
        )}
        ref={ref}
        {...props}
      >
        {hasSlots ? (
          <>
            {resolvedLeading}
            {/* Takes the slack so the slots sit at the button's edges. */}
            <span className="min-w-0 truncate">{children}</span>
            {resolvedTrailing}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
